import { useState, useEffect } from 'react'
import { T } from '../styles/theme'
import { BottomSheet, Button, Input } from './ui'
import { exportPatientsToZip, downloadBlob } from '../services/exportService'

export default function ExportModal({ patients, open, onClose }) {
  const [selected, setSelected] = useState(() => new Set(patients.map(p => p.id)))
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [anonymize, setAnonymize] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [progress, setProgress] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    if (open) {
      setSelected(new Set(patients.map(p => p.id)))
      setPassword('')
      setConfirmPassword('')
      setAnonymize(false)
      setError('')
      setProgress(null)
    }
  }, [open, patients])

  if (!open) return null

  function toggle(id) {
    setSelected(s => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleAll() {
    setSelected(s => s.size === patients.length ? new Set() : new Set(patients.map(p => p.id)))
  }

  async function handleExport() {
    setError('')
    if (selected.size === 0) { setError('Selecciona al menos un paciente'); return }
    if (password.length < 4) { setError('La contraseña debe tener al menos 4 caracteres'); return }
    if (password !== confirmPassword) { setError('Las contraseñas no coinciden'); return }

    setExporting(true)
    try {
      const chosen = patients.filter(p => selected.has(p.id))
      const blob = await exportPatientsToZip(chosen, password, (done, total) => setProgress({ done, total }), anonymize)
      const fecha = new Date().toISOString().split('T')[0]
      const filename = chosen.length === 1
        ? `nutricontrol_${(chosen[0].nombre || 'paciente').replace(/\s+/g, '_')}_${fecha}.zip`
        : `nutricontrol_export_${fecha}.zip`
      downloadBlob(blob, filename)
      onClose()
    } catch (e) {
      setError('Error al exportar: ' + e.message)
      setExporting(false)
      setProgress(null)
      return
    }
    setExporting(false)
    setProgress(null)
  }

  return (
    <BottomSheet open={open} onClose={exporting ? () => {} : onClose}>
      <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Exportar pacientes</div>
      <div style={{ fontSize: 13, color: T.muted, marginBottom: 16, lineHeight: 1.5 }}>
        Se genera un archivo .zip protegido con contraseña, con todos los datos en JSON (copia completa) y también en CSV (para abrir en Excel).
      </div>

      {patients.length > 1 && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 13, color: T.muted }}>{selected.size} de {patients.length} seleccionados</span>
            <button onClick={toggleAll} style={{ background: 'none', border: 'none', color: T.accent, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
              {selected.size === patients.length ? 'Ninguno' : 'Todos'}
            </button>
          </div>

          <div style={{ maxHeight: 180, overflowY: 'auto', marginBottom: 16, border: `1px solid ${T.cardB}`, borderRadius: 12 }}>
            {patients.map(p => (
              <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: `1px solid ${T.cardB}`, cursor: 'pointer' }}>
                <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} />
                <span style={{ fontSize: 14 }}>
                  {p.nombre}
                  {p.profesional && <span style={{ color: T.muted, fontSize: 12 }}> · {p.profesional}</span>}
                </span>
              </label>
            ))}
          </div>
        </>
      )}

      <Input label="Contraseña del archivo" type="password" placeholder="Mínimo 4 caracteres" value={password} onChange={e => setPassword(e.target.value)} />
      <Input label="Repite la contraseña" type="password" placeholder="Repite la contraseña" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
      <div style={{ fontSize: 11, color: T.muted, marginBottom: 16 }}>
        Guárdala bien: sin ella no se puede abrir el archivo, y nosotros tampoco podemos recuperarla.
      </div>

      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 16, cursor: 'pointer' }}>
        <input type="checkbox" checked={anonymize} onChange={e => setAnonymize(e.target.checked)} style={{ marginTop: 3 }} />
        <span>
          <span style={{ fontSize: 14, fontWeight: 600, display: 'block' }}>Anonimizar datos</span>
          <span style={{ fontSize: 12, color: T.muted, lineHeight: 1.4 }}>
            Sustituye el nombre por un código (PAC-001, PAC-002...) en el JSON y en todos los CSV. Útil para extraer datos con fines de estudio. Las notas clínicas de texto libre no se filtran automáticamente.
          </span>
        </span>
      </label>

      {error && (
        <div style={{ background: 'rgba(248,113,113,0.12)', border: `1px solid ${T.danger}`, borderRadius: 10, padding: '10px 14px', fontSize: 13, color: T.danger, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <Button onClick={handleExport} disabled={exporting}>
        {exporting ? (progress ? `Exportando ${progress.done}/${progress.total}...` : 'Preparando...') : `Exportar (${selected.size})`}
      </Button>
    </BottomSheet>
  )
}
